import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  message,
  Modal,
  Row,
  Switch,
} from 'antd';
import { keys } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../../../AppContext';
import {
  ALLOWED_ACTION_KEYS,
  ALLOWED_ACTION_TYPE,
  BREAKPOINTS,
  PROPERTY_TYPES,
  PROPERTY_TYPES_LABELS,
} from '../../../../../../common/constants';
import useRouter from '../../../../../../common/useRouter';
import { formValidatorRules } from '../../../../../../common/utils';
import CanPerform from '../../../../../../components/CanPerform';
import CommonSelect from '../../../../../../components/CommonSelect';
import RouterPrompt from '../../../../../../components/RouterPrompt';
import {
  CREATE_REGISTER_DETAILS,
  UPDATE_REGISTER_DETAIL,
} from '../../../../graphql/Mutation';
import { GET_REGISTER } from '../../../../graphql/Queries';
import CommonRegisterOptionUi from './CommonRegisterOptionUi';

const { required, characterWithoutWhiteSpace } = formValidatorRules;

const AddEditPropertyModal = ({
  showModal,
  setShowModal,
  propertyDetails,
  setPropertyDetails,
  setTableColumns,
  isDisabled = false,
  selectedProperty,
  setSelectedProperty,
}) => {
  const {
    params: { registerId },
  } = useRouter();
  const { state, dispatch } = useContext(AppContext);
  const [form] = Form.useForm();
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const [displayOption, setDisplayOption] = useState(false);
  const [isOptionType, setIsOptionType] = useState(false);
  const optionsInputRef = useRef(null);

  const showRouterPrompt = (value) => {
    dispatch({
      type: 'SET_SHOW_PROMPT',
      data: value,
    });
  };

  const [getRegister] = useLazyQuery(GET_REGISTER, {
    fetchPolicy: 'network-only',
    onError() {},
    onCompleted(response) {
      const registerDetails = response?.getRegister?.registerDetails;
      const registerFormFields = response?.getRegister?.registerFormFields;
      setPropertyDetails(registerDetails);
      setTableColumns(registerFormFields);
      setShowModal(false);
    },
  });

  const [createRegisterDetails] = useMutation(CREATE_REGISTER_DETAILS, {
    fetchPolicy: 'network-only',
    onError() {},
    onCompleted() {
      getRegister({ variables: { getRegisterId: registerId } });
    },
  });

  const [updateRegisterDetails] = useMutation(UPDATE_REGISTER_DETAIL, {
    fetchPolicy: 'network-only',
    onError() {},
    onCompleted() {
      setSelectedProperty();
      getRegister({ variables: { getRegisterId: registerId } });
    },
  });

  const handleTypeChange = (options) => {
    switch (options?.value) {
      case PROPERTY_TYPES.OPTIONS:
        setDisplayOption(true);
        setIsOptionType(true);
        break;

      case PROPERTY_TYPES.CHECKBOXES:
        setDisplayOption(true);
        setIsOptionType(true);
        break;

      default:
        setDisplayOption(false);
        setIsOptionType(false);
        break;
    }
  };

  useEffect(() => {
    if (selectedProperty) {
      const transformedValues = {
        ...selectedProperty,
        options:
          selectedProperty?.options?.map((option) => ({ option })) || null,
      };
      form.setFieldsValue(transformedValues);
      handleTypeChange({ value: selectedProperty?.type });
    }
  }, [selectedProperty]);

  const handleSelect = (labeledValue, options) => {
    const existingOptions = form.getFieldValue('options');
    handleTypeChange(options);
    if (labeledValue === PROPERTY_TYPES.OPTIONS) {
      form.setFieldsValue({
        options: existingOptions?.length ? existingOptions : [{}, {}],
      });
    } else if (labeledValue === PROPERTY_TYPES.CHECKBOXES) {
      form.setFieldsValue({
        options: existingOptions?.length ? existingOptions : [{}, {}],
      });
    }
  };

  const handleCancel = () => {
    showRouterPrompt(false);
    setShowModal(false);
    setSelectedProperty();
  };

  const onPropertySubmitFinish = async () => {
    const formValues = form.getFieldsValue();
    const options = formValues?.options?.map((value) => value?.option);
    if (registerId) {
      if (!selectedProperty) {
        if (propertyDetails?.find((item) => item?.name === formValues?.name)) {
          message.destroy();
          message.error('Detail name already exists.');
        } else {
          await createRegisterDetails({
            variables: {
              data: {
                ...formValues,
                options: options || null,
                registerId,
                sequence:
                  propertyDetails?.[propertyDetails?.length - 1]?.sequence +
                    1 || 1,
              },
            },
          });
          showRouterPrompt(false);
        }
      } else if (
        propertyDetails?.find(
          (item) =>
            item?.name === formValues?.name &&
            item?.id !== selectedProperty?.id,
        )
      ) {
        message.destroy();
        message.error('Detail name already exists.');
      } else {
        await updateRegisterDetails({
          variables: {
            updateRegisterDetailId: selectedProperty?.id,
            data: {
              ...formValues,
              options: options || null,
              sequence: selectedProperty?.sequence,
            },
          },
        });
        showRouterPrompt(false);
      }
    }
  };

  return (
    <div>
      <RouterPrompt openPrompt={state?.showPrompt} />
      <Modal
        open={showModal}
        footer={false}
        centered
        className="stageitem-modal"
        maskClosable={false}
        closable={false}
        form={form}
      >
        <h2 className="mb-15">{selectedProperty ? 'Edit ' : 'Add '} Detail</h2>
        <Form
          layout="vertical"
          form={form}
          initialValues={{
            type: 'TEXT',
            isRequired: true,
            options: [{}, {}],
          }}
          onFieldsChange={() => {
            showRouterPrompt(true);
          }}
          className="mt-16"
          onFinish={onPropertySubmitFinish}
        >
          <Form.Item
            rules={[
              required,
              characterWithoutWhiteSpace,
              { max: 250, message: 'Maximum 250 characters are allowed' },
            ]}
            name="name"
            label="Property Name"
          >
            <Input
              placeholder="Enter property name"
              disabled={isDisabled}
              allowClear
            />
          </Form.Item>
          <Row gutter={30}>
            <Col span={isDesktopViewport ? 18 : 24}>
              <Form.Item name="type" label="Type">
                <CommonSelect
                  className="mr-3"
                  placeholder="Select Type"
                  onSelect={handleSelect}
                  options={[
                    ...keys(PROPERTY_TYPES).map((type) => {
                      return {
                        key: PROPERTY_TYPES_LABELS[type],
                        value: type,
                        label: PROPERTY_TYPES_LABELS[type],
                      };
                    }),
                  ]}
                  disabled={isDisabled}
                />
              </Form.Item>
            </Col>
            <Col span={isDesktopViewport ? 6 : 24}>
              <Form.Item
                name="isRequired"
                label="Required"
                valuePropName="checked"
              >
                <Switch defaultChecked disabled={isDisabled} />
              </Form.Item>
            </Col>
          </Row>
          {displayOption && (
            <>
              <Divider dashed />
              {isOptionType && <h3>Options</h3>}
              {displayOption && (
                <CommonRegisterOptionUi
                  isDisabled={isDisabled}
                  form={form}
                  optionsInputRef={optionsInputRef}
                />
              )}
            </>
          )}
          <div className="form-buttons">
            <Button
              shape="round"
              className="cancel-button"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <CanPerform
              action={ALLOWED_ACTION_KEYS.ADD_PROJECT_REGISTER}
              type={ALLOWED_ACTION_TYPE.BOTH}
            >
              {!isDisabled && (
                <Button
                  shape="round"
                  type="primary"
                  className="save-button"
                  htmlType="submit"
                >
                  Save
                </Button>
              )}
            </CanPerform>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AddEditPropertyModal;
